import { toDate, toMajorString, toZonedDateKey, type Money } from '@nucleus-commerce/core';

export interface FormatterOptions {
  /** BCP-47 locale, e.g. "en-US". */
  locale: string;
  /** IANA timezone the store operates in, e.g. "Asia/Kolkata". */
  timeZone: string;
}

export type DateInput = Date | string;

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 1000 * 60 * 60 * 24 * 365],
  ['month', 1000 * 60 * 60 * 24 * 30],
  ['week', 1000 * 60 * 60 * 24 * 7],
  ['day', 1000 * 60 * 60 * 24],
  ['hour', 1000 * 60 * 60],
  ['minute', 1000 * 60],
  ['second', 1000],
];

/**
 * Locale + timezone aware formatters. Create once per (locale, timeZone) and reuse;
 * Intl constructors are expensive.
 */
export function createFormatters({ locale, timeZone }: FormatterOptions) {
  const numberCache = new Map<string, Intl.NumberFormat>();
  const dateCache = new Map<string, Intl.DateTimeFormat>();

  const numberFormat = (opts: Intl.NumberFormatOptions) => {
    const key = JSON.stringify(opts);
    let f = numberCache.get(key);
    if (!f) {
      f = new Intl.NumberFormat(locale, opts);
      numberCache.set(key, f);
    }
    return f;
  };

  const dateFormat = (opts: Intl.DateTimeFormatOptions) => {
    const key = JSON.stringify(opts);
    let f = dateCache.get(key);
    if (!f) {
      f = new Intl.DateTimeFormat(locale, { timeZone, ...opts });
      dateCache.set(key, f);
    }
    return f;
  };

  const asDate = (value: DateInput) => (value instanceof Date ? value : toDate(value));

  return {
    locale,
    timeZone,

    /** { 129900, USD } → "$1,299.00". Minor units become an exact decimal string; no float. */
    money(value: Money, opts?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>): string {
      return numberFormat({ style: 'currency', currency: value.currency, ...opts }).format(
        toMajorString(value) as Intl.StringNumericLiteral,
      );
    },

    number(value: number | string, opts?: Intl.NumberFormatOptions): string {
      return numberFormat(opts ?? {}).format(value as Intl.StringNumericLiteral);
    },

    /** 0.125 → "12.5%". */
    percent(ratio: number, opts?: Intl.NumberFormatOptions): string {
      return numberFormat({ style: 'percent', maximumFractionDigits: 1, ...opts }).format(ratio);
    },

    /** "25 Sep 2026" in the store timezone. */
    date(value: DateInput, dateStyle: Intl.DateTimeFormatOptions['dateStyle'] = 'medium'): string {
      return dateFormat({ dateStyle }).format(asDate(value));
    },

    /** "25 Sep 2026, 14:05" in the store timezone. */
    dateTime(
      value: DateInput,
      opts: Pick<Intl.DateTimeFormatOptions, 'dateStyle' | 'timeStyle'> = {
        dateStyle: 'medium',
        timeStyle: 'short',
      },
    ): string {
      return dateFormat(opts).format(asDate(value));
    },

    time(value: DateInput, timeStyle: Intl.DateTimeFormatOptions['timeStyle'] = 'short'): string {
      return dateFormat({ timeStyle }).format(asDate(value));
    },

    /** The same instant in UTC, for tooltips: "2026-09-25 08:35 UTC". */
    utc(value: DateInput): string {
      const d = asDate(value);
      return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
    },

    /** "3 hours ago" / "in 2 days". */
    relative(value: DateInput, now: Date = new Date()): string {
      const diff = asDate(value).getTime() - now.getTime();
      const abs = Math.abs(diff);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      for (const [unit, ms] of RELATIVE_UNITS) {
        if (abs >= ms || unit === 'second') {
          return rtf.format(Math.round(diff / ms), unit);
        }
      }
      return rtf.format(0, 'second');
    },

    /** "YYYY-MM-DD" as seen in the store timezone, for date inputs and report ranges. */
    dateKey(value: DateInput): string {
      return toZonedDateKey(asDate(value), timeZone);
    },
  };
}

export type Formatters = ReturnType<typeof createFormatters>;
